
import { supabase, invokeFunction } from '@/integrations/supabase/client';

export const EXPORT_STATUS = {
  queued: 'queued',
  processing: 'processing',
  combining: 'combining',
  completed: 'completed',
  failed: 'failed',
} as const;
export type ExportStatus = typeof EXPORT_STATUS[keyof typeof EXPORT_STATUS];

export interface BackgroundExportRequest {
  connectionId: string;
  schema: string;
  tableName: string;
  filters?: Array<{ column: string; value: string; operation: string }>;
  customSQL?: string;
  queryFocus?: string;
}

export interface BackgroundExportStatus {
  export_id: string;
  status: ExportStatus;
  file_path?: string;
  download_url?: string;
  record_count?: number;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  connection_id: string;
  schema_name: string;
  table_name: string;
  filters?: Array<{ column: string; value: string; operation: string }>;
  current_step?: string;
  estimated_row_count?: number;
  actual_row_count?: number;
  processing_duration_ms?: number;
  failure_step?: string;
  chunks_completed?: number;
  chunk_count?: number;
  chunk_files?: any; // JSON array of chunk file paths
  organisation_id?: string;
}

export const startBackgroundExport = async (request: BackgroundExportRequest): Promise<{ success: boolean; export_id?: string; error?: string }> => {
  try {
    const exportId = `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Check if this is a report export (connection_id starts with 'report-')
    const isReportExport = request.connectionId.startsWith('report-');
    let parsedReportParams: any = null;
    if (isReportExport && request.customSQL) {
      try {
        parsedReportParams = JSON.parse(request.customSQL);
      } catch (parseError) {
        console.error('Invalid report params JSON:', parseError);
        return { success: false, error: 'Invalid report parameters payload' };
      }
    }

    // Get organisation_id from connection_id by searching in connections jsonb array
    const { data: orgs } = await supabase
      .from('organisations')
      .select('id, connections');

    // Find org that has this connectionId in its connections array (skip for reports)
    const matchingOrg = isReportExport ? null : orgs?.find(org => {
      const connections = org.connections as any[];
      return connections?.some(conn => conn.connectionId === request.connectionId);
    });

    // For reports, extract org ID from customSQL
    let organisationId = matchingOrg?.id || null;
    if (parsedReportParams?.organisationId) {
      organisationId = parsedReportParams.organisationId;
    }

    // Create export record first
    const { error: insertError } = await supabase
      .from('csv_exports')
      .insert({
        export_id: exportId,
        connection_id: request.connectionId,
        schema_name: request.schema,
        table_name: request.tableName,
        status: 'queued',
        filters: request.filters || null,
        custom_sql: request.customSQL || null,
        query_focus: request.queryFocus || null,
        is_worker_export: false,
        organisation_id: organisationId
      });

    if (insertError) {
      console.error('Error creating export record:', insertError);
      return { success: false, error: insertError.message };
    }

    // Use specialized report export handler for reports
    if (isReportExport && request.customSQL) {
      const reportParams = parsedReportParams;

      const exportFunction = 'background-report-export-v7'

      const { error: functionError } = await invokeFunction(supabase, exportFunction, {
        body: {
          exportId,
          reportParams
        }
      });

      if (functionError) {
        console.error('Error starting report export:', functionError);
        await supabase
          .from('csv_exports')
          .update({
            status: 'failed',
            error_message: functionError.message,
            completed_at: new Date().toISOString()
          })
          .eq('export_id', exportId);
        return { success: false, error: functionError.message };
      }
    } else {
      // Regular coordinator for database exports
      const { error: functionError } = await invokeFunction(supabase, 'background-csv-export-coordinator', {
        body: {
          exportId: exportId,
          connectionId: request.connectionId,
          schema: request.schema,
          tableName: request.tableName,
          filters: request.filters
        }
      });

      if (functionError) {
        console.error('Error starting background export:', functionError);
        await supabase
          .from('csv_exports')
          .update({
            status: 'failed',
            error_message: functionError.message,
            completed_at: new Date().toISOString()
          })
          .eq('export_id', exportId);
        return { success: false, error: functionError.message };
      }
    }

    return { success: true, export_id: exportId };
  } catch (error) {
    console.error('Background export request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getExportStatus = async (exportId: string): Promise<{ success: boolean; status?: BackgroundExportStatus; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('csv_exports')
      .select('*')
      .eq('export_id', exportId)
      .maybeSingle();

    if (error) {
      console.error('Export status fetch error:', error);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: 'Export not found - may have been cleaned up or expired' };
    }

    // Convert the data to BackgroundExportStatus with proper type casting
    const status: BackgroundExportStatus = {
      ...data,
      status: data.status as ExportStatus,
      filters: data.filters as Array<{ column: string; value: string; operation: string }> | undefined
    };

    // Calculate processing duration if still running
    if ((status.status === 'processing' || status.status === 'combining') && status.started_at && !status.processing_duration_ms) {
      const startTime = new Date(status.started_at).getTime();
      const now = Date.now();
      status.processing_duration_ms = now - startTime;
    }
    return { success: true, status };
  } catch (error) {
    console.error('Export status request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getAllExportsForConnection = async (connectionId: string): Promise<{ success: boolean; exports?: BackgroundExportStatus[]; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('csv_exports')
      .select('*')
      .eq('connection_id', connectionId)
      .eq('is_worker_export', false) // Only get main exports, not worker sub-exports
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Exports fetch error:', error);
      return { success: false, error: error.message };
    }

    // Convert the data array to BackgroundExportStatus[] with proper type casting
    const exports: BackgroundExportStatus[] = (data || []).map(item => {
      const exportItem: BackgroundExportStatus = {
        ...item,
        status: item.status as ExportStatus,
        filters: item.filters as Array<{ column: string; value: string; operation: string }> | undefined
      };

      // Calculate processing duration if still running
      if ((exportItem.status === 'processing' || exportItem.status === 'combining') && exportItem.started_at && !exportItem.processing_duration_ms) {
        const startTime = new Date(exportItem.started_at).getTime();
        const now = Date.now();
        exportItem.processing_duration_ms = now - startTime;
      }

      return exportItem;
    });

    return { success: true, exports };
  } catch (error) {
    console.error('Exports request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export interface DownloadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export const downloadExportFile = async (
  downloadUrl: string,
  filename: string,
  onProgress?: (progress: DownloadProgress) => void,
  exportId?: string
): Promise<void> => {
  try {
    let resolvedUrl = downloadUrl;

    // Get a fresh signed URL via edge function (handles private buckets + expired URLs)
    if (exportId) {
      try {
        const { data, error } = await invokeFunction(supabase, 'get-export-download-url', {
          body: { exportId }
        });
        if (!error && data?.signedUrl) {
          resolvedUrl = data.signedUrl;
        }
      } catch (urlError) {
        console.warn('Failed to get fresh download URL, trying original:', urlError);
      }
    } else if (downloadUrl.includes('/storage/v1/object/sign/')) {
      // Fallback: try client-side refresh for legacy calls without exportId
      try {
        const urlObj = new URL(downloadUrl);
        const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/sign\/([^/]+)\/(.+)/);
        if (pathMatch) {
          const [, bucket, filePath] = pathMatch;
          const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(decodeURIComponent(filePath), 3600);
          if (!error && data?.signedUrl) {
            resolvedUrl = data.signedUrl;
          }
        }
      } catch (urlError) {
        console.warn('Failed to refresh signed URL, trying original:', urlError);
      }
    }

    console.log("Starting streaming download", resolvedUrl, filename);

    const response = await fetch(resolvedUrl);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;

    // Read the response stream
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      loaded += value.length;

      // Report progress without total size (streaming download)
      if (onProgress) {
        onProgress({
          loaded,
          total: 0, // Unknown for streaming
          percentage: 0 // Can't calculate percentage without total
        });
      }
    }

    // Combine chunks into a single buffer to satisfy BlobPart typing
    const combined = new Uint8Array(loaded);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    const blob = new Blob([combined.buffer], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    console.log(`Streaming download completed: ${loaded} bytes`);

  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

/**
 * Cancel a background export (mark as failed)
 */
export const cancelBackgroundExport = async (exportId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('csv_exports')
      .update({
        status: 'failed',
        error_message: 'Export cancelled by user',
        completed_at: new Date().toISOString(),
        failure_step: 'USER_CANCELLED'
      })
      .eq('export_id', exportId)
      .in('status', ['queued', 'processing', 'combining']);

    if (error) {
      console.error('Failed to cancel export:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Cancel export request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Clean up stuck exports automatically (legacy - use intelligent cleanup for better results)
 */
export const cleanupStuckExports = async (): Promise<{ success: boolean; cleaned_count?: number; error?: string }> => {
  try {
    const { data, error } = await supabase
      .from('csv_exports')
      .update({
        status: 'failed',
        error_message: 'Export was stuck and automatically cleaned up',
        completed_at: new Date().toISOString(),
        failure_step: 'STUCK_CLEANUP'
      })
      .in('status', ['queued', 'processing', 'combining'])
      .lt('created_at', new Date(Date.now() - 10 * 60 * 1000).toISOString()) // 10 minutes ago
      .select('export_id');

    if (error) {
      console.error('Failed to cleanup stuck exports:', error);
      return { success: false, error: error.message };
    }

    console.log(`Cleaned up ${data?.length || 0} stuck exports`);
    return { success: true, cleaned_count: data?.length || 0 };
  } catch (error) {
    console.error('Cleanup stuck exports request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Intelligent cleanup that fixes completed exports and only marks truly stuck ones as failed
 */
export const intelligentCleanupStuckExports = async (maxAgeMinutes: number = 30): Promise<{
  success: boolean;
  fixed_count?: number;
  cleaned_count?: number;
  actions?: Array<{ action_taken: string; export_id: string; reason: string }>;
  error?: string
}> => {
  try {
    const { data, error } = await supabase.rpc('cleanup_stuck_exports_intelligent', {
      max_age_minutes: maxAgeMinutes
    });

    if (error) {
      console.error('Failed to run intelligent cleanup:', error);
      return { success: false, error: error.message };
    }

    const actions = data || [];
    const fixedCount = actions.filter((item: any) => item.action_taken === 'fixed_completed').length;
    const cleanedCount = actions.filter((item: any) => item.action_taken === 'marked_failed').length;

    console.log(`Intelligent cleanup: Fixed ${fixedCount}, Cleaned ${cleanedCount} exports`);
    return {
      success: true,
      fixed_count: fixedCount,
      cleaned_count: cleanedCount,
      actions: actions
    };
  } catch (error) {
    console.error('Intelligent cleanup request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Retry a failed background export
 */
export const retryBackgroundExport = async (exportId: string): Promise<{ success: boolean; new_export_id?: string; error?: string }> => {
  try {
    // Get the original export details
    const { data: originalExport, error: fetchError } = await supabase
      .from('csv_exports')
      .select('*')
      .eq('export_id', exportId)
      .maybeSingle();

    if (fetchError || !originalExport) {
      return { success: false, error: 'Original export not found' };
    }

    // Start a new export with the same parameters
    const retryRequest: BackgroundExportRequest = {
      connectionId: originalExport.connection_id,
      schema: originalExport.schema_name,
      tableName: originalExport.table_name,
      filters: originalExport.filters as Array<{ column: string; value: string; operation: string }> || []
    };

    const result = await startBackgroundExport(retryRequest);

    return result;
  } catch (error) {
    console.error('Retry export request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Trigger completion detection for stuck exports with completed chunks
 */
export const triggerCompletionDetection = async (): Promise<{ success: boolean; triggered_exports?: string[]; error?: string }> => {
  try {
    const { data, error } = await invokeFunction(supabase, 'background-csv-export-coordinator', {
      body: { detectCompletion: true }
    });

    if (error) {
      console.error('Failed to trigger completion detection:', error);
      return { success: false, error: error.message };
    }

    return {
      success: true,
      triggered_exports: data?.exports || []
    };
  } catch (error) {
    console.error('Completion detection request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Manually prepare streaming download for a specific export
 */
export const manualPrepareStreaming = async (exportId: string, totalChunks: number): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await invokeFunction(supabase, 'background-csv-export-coordinator', {
      body: {
        combineChunks: true,
        exportId: exportId,
        totalChunks: totalChunks
      }
    });

    if (error) {
      console.error('Failed to prepare streaming download:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Streaming preparation request failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Remove all artifacts for a completed/failed export:
 * - Delete chunk files under company-files/exports/{exportId}
 * - Delete related csv_export_chunks rows
 * - Delete csv_exports row
 */
export const cleanupExportArtifacts = async (exportId: string): Promise<{
  success: boolean;
  deletedFiles?: number;
  error?: string
}> => {
  try {
    let deletedFiles = 0;

    // 1) List files in storage folder and remove them
    const { data: files, error: listError } = await supabase.storage
      .from('company-files')
      .list(`exports/${exportId}`, { limit: 10000 });

    if (listError) {
      console.warn('Cleanup: failed to list export files (continuing):', listError);
    } else if (files && files.length > 0) {
      const paths = files.map((f: any) => `exports/${exportId}/${f.name}`);
      const { data: removed, error: removeError } = await supabase.storage
        .from('company-files')
        .remove(paths);
      if (removeError) {
        console.warn('Cleanup: failed to remove some files (continuing):', removeError);
      } else {
        deletedFiles = removed?.length || 0;
      }
    }

    // 2) Delete chunk records first (FK safety)
    const { error: deleteChunksError } = await supabase
      .from('csv_export_chunks')
      .delete()
      .eq('export_id', exportId);

    if (deleteChunksError) {
      console.warn('Cleanup: failed to delete chunk rows (continuing):', deleteChunksError);
    }

    // 3) Delete export record
    const { error: deleteExportError } = await supabase
      .from('csv_exports')
      .delete()
      .eq('export_id', exportId);

    if (deleteExportError) {
      console.error('Cleanup: failed to delete export row:', deleteExportError);
      return { success: false, deletedFiles, error: deleteExportError.message };
    }

    return { success: true, deletedFiles };
  } catch (error) {
    console.error('Cleanup export artifacts failed:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};
